import { EntityReference, type DataSource } from "@firecms/core";
import {
  getContentBasePath,
  type ContentScopeId,
} from "../workspace/collectionIds";
import type { ContentImportCollection, ContentImportItemDto } from "./types";
import { resolveUnitCodeForImport } from "./resolveUnitCodeForImport";

const buildUnitsPath = (id: ContentScopeId) => `${getContentBasePath(id)}/units`;
const buildStoriesPath = (id: ContentScopeId) =>
  `${getContentBasePath(id)}/stories`;
const buildLevelsPath = (id: ContentScopeId) =>
  `${getContentBasePath(id)}/levels`;

export type ReferenceMaps = {
  unitByCode: Map<string, string>;
  storyByCode: Map<string, string>;
  levelByCode: Map<string, string>;
};

export async function loadReferenceMaps(
  dataSource: DataSource,
  scopeId: ContentScopeId
): Promise<ReferenceMaps> {
  const unitsPath = buildUnitsPath(scopeId);
  const storiesPath = buildStoriesPath(scopeId);
  const levelsPath = buildLevelsPath(scopeId);

  const [units, stories, levels] = await Promise.all([
    dataSource.fetchCollection({ path: unitsPath }),
    dataSource.fetchCollection({ path: storiesPath }),
    dataSource.fetchCollection({ path: levelsPath }),
  ]);

  const unitByCode = new Map<string, string>();
  for (const entity of units) {
    const code = entity.values?.code;
    if (typeof code === "string") {
      unitByCode.set(code, entity.id);
    }
  }

  const storyByCode = new Map<string, string>();
  for (const entity of stories) {
    const code = entity.values?.code;
    if (typeof code === "string") {
      storyByCode.set(code, entity.id);
    }
  }

  const levelByCode = new Map<string, string>();
  for (const entity of levels) {
    const code = entity.values?.code;
    if (typeof code === "string") {
      levelByCode.set(code, entity.id);
    }
  }

  return { unitByCode, storyByCode, levelByCode };
}

function unitRef(scopeId: ContentScopeId, unitDocId: string): EntityReference {
  return new EntityReference(unitDocId, buildUnitsPath(scopeId));
}

function storyRef(scopeId: ContentScopeId, storyDocId: string): EntityReference {
  return new EntityReference(storyDocId, buildStoriesPath(scopeId));
}

export function resolveQuestionReferences(
  item: ContentImportItemDto,
  scopeId: ContentScopeId,
  maps: ReferenceMaps
): { values: Record<string, unknown> } | { error: string } {
  const resolved = resolveUnitCodeForImport(item.unitCode, maps.unitByCode);
  if ("error" in resolved) {
    return resolved;
  }

  const { unitCode: _removed, ...rest } = item;
  return {
    values: {
      ...rest,
      unitRef: unitRef(scopeId, resolved.unitDocId),
    },
  };
}

export function resolveStoryReferences(
  item: ContentImportItemDto,
  scopeId: ContentScopeId,
  maps: ReferenceMaps
): { values: Record<string, unknown> } | { error: string } {
  const resolved = resolveUnitCodeForImport(item.unitCode, maps.unitByCode);
  if ("error" in resolved) {
    return resolved;
  }

  const { unitCode: _removed, ...rest } = item;
  return {
    values: {
      ...rest,
      unitRef: unitRef(scopeId, resolved.unitDocId),
    },
  };
}

export function resolveLevelReferences(
  item: ContentImportItemDto,
  scopeId: ContentScopeId,
  maps: ReferenceMaps
): { values: Record<string, unknown> } | { error: string } {
  const resolved = resolveUnitCodeForImport(item.unitCode, maps.unitByCode);
  if ("error" in resolved) {
    return resolved;
  }

  const { unitCode: _removed, storyCode, ...rest } = item;
  const values: Record<string, unknown> = {
    ...rest,
    unitRef: unitRef(scopeId, resolved.unitDocId),
  };

  if (item.modality === "LEVEL_MODALITY_STORY") {
    if (typeof storyCode !== "string") {
      return { error: "storyCode is required for LEVEL_MODALITY_STORY" };
    }
    const storyDocId = maps.storyByCode.get(storyCode);
    if (!storyDocId) {
      return { error: `Story not found for code: ${storyCode}` };
    }
    values.storyRef = storyRef(scopeId, storyDocId);
    delete values.questionDifficulties;
    values.prizeAmount = "PRIZE_AMOUNT_NONE";
  } else if (
    item.modality === "LEVEL_MODALITY_MAZE" ||
    item.modality === "LEVEL_MODALITY_REWARD"
  ) {
    delete values.questionDifficulties;
  }

  return { values };
}

export function resolveImportReferences(
  collection: ContentImportCollection,
  item: ContentImportItemDto,
  scopeId: ContentScopeId,
  maps: ReferenceMaps
): { values: Record<string, unknown> } | { error: string } {
  switch (collection) {
    case "units":
      return { values: { ...item } };
    case "questions":
      return resolveQuestionReferences(item, scopeId, maps);
    case "stories":
      return resolveStoryReferences(item, scopeId, maps);
    case "levels":
      return resolveLevelReferences(item, scopeId, maps);
  }
}
